// @ts-check
// Mudança de etapa que precisa de dado junto: ao ir para Visita agendada a consultora informa a data da visita
// (base dos relatórios de visita); ao ir para Perdido, o motivo (o motor recusa Perdido sem motivo); ao ir para Não é
// lead, o motivo de não ser lead (também obrigatório). O diálogo vale para o kanban, a tabela, a página do card e as
// ações "Não é lead", que chamam resolveStageTransition antes de gravar. Antes de avançar, a tela pergunta ao motor o
// que falta (campos obrigatórios para avançar, contrato 08 do modelo de dados, seção 6) e, se faltar algo, abre o
// formulário com esses campos (junto da data da visita quando o destino é Visita agendada). Destino em Pode passar
// (papel restrito por etapa, contrato de equipe, seção 3.2) pede confirmação: o card sai da visão de quem o move.
import { html } from 'htm/preact';
import { signal } from '@preact/signals';
import { useEffect, useState } from 'preact/hooks';
import { currentUserPermissionsState } from '../../data/metadata-store.js';
import { getSortedOptions } from '../../lib/field-metadata.js';
import { isHandoffStage } from '../../lib/permissions.js';
import { listHandoffMembers } from '../../data/team.js';
import { formatMemberName } from '../../lib/field-metadata.js';
import { NOT_A_LEAD_STAGE } from '../../../../shared/metadata/funnel-stages.js';
import { Modal } from '../../ui/modal.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import { toLocalDateTimeInput } from './field-input.js';
import { checkStageRequirements } from '../../data/stage-requirements.js';
import { StageRequirementsForm } from './stage-requirements-form.js';

const CARD_OBJECT_NAME = 'enrollmentOpportunity';
const STAGE_FIELD_NAME = 'stage';
const VISIT_SCHEDULED_STAGE = 'VISIT_SCHEDULED';
const VISIT_DONE_STAGE = 'VISIT_DONE';
const LOST_STAGE = 'LOST';
const VISIT_DATE_FIELD_NAME = 'visitScheduledAt';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('./field-input.js').FieldPatch} FieldPatch
 * @typedef {import('../../data/stage-requirements.js').MissingRequirement} MissingRequirement
 * @typedef {{ kind: 'VISIT_DATE' | 'LOST_REASON' | 'NOT_LEAD_REASON' | 'REQUIREMENTS' | 'HANDOFF', objectMetadata: ObjectMetadata, record: any,
 *   patch: NonNullable<FieldPatch>, resolve: (patch: FieldPatch) => void, nextStage: string, missing: MissingRequirement[], isHandoff: boolean }} PendingTransition
 */

/** @type {import('@preact/signals').Signal<PendingTransition | null>} */
const pendingTransitionState = signal(null);

/**
 * Patch final da mudança: o mesmo, um patch com o dado pedido no diálogo, ou null se a pessoa cancelar.
 * @param {ObjectMetadata} objectMetadata @param {any} record @param {FieldPatch} patch
 * @returns {Promise<FieldPatch>}
 */
export async function resolveStageTransition(objectMetadata, record, patch) {
  if (!patch || objectMetadata.nameSingular !== CARD_OBJECT_NAME) return patch;
  const nextStage = patch.apiPatch[STAGE_FIELD_NAME];
  if (typeof nextStage !== 'string' || nextStage === record?.[STAGE_FIELD_NAME]) return patch;
  /** @type {PendingTransition['kind'] | null} */
  let kind = null;
  /** @type {MissingRequirement[]} */
  let missing = [];
  // Perdido e Não é lead fecham o card: só pedem o motivo, nunca os campos obrigatórios
  if (nextStage !== LOST_STAGE && nextStage !== NOT_A_LEAD_STAGE && record?.id) {
    // sem resposta da consulta segue o fluxo normal: o motor recusa e o snackbar lista o que falta
    missing = await checkStageRequirements(record.id, nextStage).catch(() => []);
  }
  const isHandoff = isHandoffStage(currentUserPermissionsState.value, nextStage);
  if (missing.length > 0) kind = 'REQUIREMENTS';
  else if (nextStage === VISIT_SCHEDULED_STAGE) kind = 'VISIT_DATE';
  else if (nextStage === LOST_STAGE && !record?.lostReason && !patch.apiPatch.lostReason) kind = 'LOST_REASON';
  else if (nextStage === NOT_A_LEAD_STAGE && !patch.apiPatch.notLeadReason) kind = 'NOT_LEAD_REASON';
  else if (isHandoff) kind = 'HANDOFF';
  if (!kind) return patch;
  const pendingKind = kind;
  return new Promise((resolve) => {
    pendingTransitionState.value = { kind: pendingKind, objectMetadata, record, patch, resolve, nextStage, missing, isHandoff };
  });
}

/**
 * Depois da visita realizada a data da visita só muda pelo administrador (o motor também confere).
 * @param {ObjectMetadata} objectMetadata @param {{ name: string }} field @param {any} record
 */
export function isFieldLockedByStage(objectMetadata, field, record) {
  if (objectMetadata.nameSingular !== CARD_OBJECT_NAME || field.name !== VISIT_DATE_FIELD_NAME || !record) return false;
  if (currentUserPermissionsState.value?.isAdministrator) return false;
  const stageField = objectMetadata.fields.find((candidate) => candidate.name === STAGE_FIELD_NAME);
  const options = stageField ? getSortedOptions(stageField) : [];
  const visitIndex = options.findIndex((option) => option.value === VISIT_DONE_STAGE);
  const stageIndex = options.findIndex((option) => option.value === record[STAGE_FIELD_NAME]);
  return visitIndex >= 0 && stageIndex >= visitIndex && record[STAGE_FIELD_NAME] !== LOST_STAGE && record[STAGE_FIELD_NAME] !== NOT_A_LEAD_STAGE;
}

/** Montado uma vez no app, junto dos avisos. */
export function StageTransitionDialog() {
  const pending = pendingTransitionState.value;
  if (!pending) return null;
  return html`<${StageTransitionForm} key=${`${pending.record?.id}:${pending.kind}`} pending=${pending} />`;
}

/** @param {{ pending: PendingTransition }} props */
function StageTransitionForm({ pending }) {
  const { kind, objectMetadata, record, patch, resolve } = pending;
  const [visitDate, setVisitDate] = useState(record?.[VISIT_DATE_FIELD_NAME] ? toLocalDateTimeInput(record[VISIT_DATE_FIELD_NAME]) : '');
  const [lostReason, setLostReason] = useState('');
  const [lostReasonDetail, setLostReasonDetail] = useState('');
  const finish = (/** @type {FieldPatch} */ result) => {
    pendingTransitionState.value = null;
    resolve(result);
  };
  // formulários sem o aviso de repasse embutido terminam na confirmação de Pode passar
  const finishOrConfirmHandoff = (/** @type {FieldPatch} */ result) => {
    if (!result || !pending.isHandoff) return finish(result);
    pendingTransitionState.value = { ...pending, kind: 'HANDOFF', patch: result };
  };
  const recordLabel = record?.name ?? '';
  const stageLabel = stageLabelOf(objectMetadata, pending.nextStage);
  const handoffNote = pending.isHandoff ? html`<${HandoffNote} record=${record} stageLabel=${stageLabel} />` : null;

  if (kind === 'HANDOFF') return html`<${HandoffConfirm} pending=${pending} stageLabel=${stageLabel} finish=${finish} />`;

  if (kind === 'REQUIREMENTS') {
    return html`<${StageRequirementsForm}
      objectMetadata=${objectMetadata}
      record=${record}
      nextStage=${pending.nextStage}
      initialMissing=${pending.missing}
      includeVisitDate=${pending.nextStage === VISIT_SCHEDULED_STAGE && !isFieldLockedByStage(objectMetadata, { name: VISIT_DATE_FIELD_NAME }, record)}
      onCancel=${() => finish(null)}
      onDone=${(/** @type {Record<string, unknown>} */ extra) => finishOrConfirmHandoff({ apiPatch: { ...patch.apiPatch, ...extra }, localPatch: { ...patch.localPatch, ...extra } })}
    />`;
  }

  if (kind === 'VISIT_DATE') {
    const isLocked = isFieldLockedByStage(objectMetadata, { name: VISIT_DATE_FIELD_NAME }, record);
    return html`<${Modal}
      title=${t('stageTransition.visitTitle')}
      subtitle=${t('stageTransition.visitSubtitle', { record: recordLabel })}
      confirmLabel=${t('stageTransition.confirm')}
      isConfirmDisabled=${!visitDate}
      onClose=${() => finish(null)}
      onConfirm=${() => {
        if (isLocked) return finishOrConfirmHandoff(patch);
        const iso = new Date(visitDate).toISOString();
        finishOrConfirmHandoff({ apiPatch: { ...patch.apiPatch, [VISIT_DATE_FIELD_NAME]: iso }, localPatch: { ...patch.localPatch, [VISIT_DATE_FIELD_NAME]: iso } });
      }}
    >
      <label class="stage-transition-field">
        <span>${t('stageTransition.visitDate')}</span>
        <input class="text-input" type="datetime-local" required value=${visitDate} disabled=${isLocked}
          ref=${(/** @type {HTMLInputElement | null} */ element) => element && !element.dataset.focused && (element.dataset.focused = 'true') && requestAnimationFrame(() => element.focus())}
          onInput=${(/** @type {Event} */ event) => setVisitDate(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
      </label>
    <//>`;
  }

  if (kind === 'NOT_LEAD_REASON') return html`<${NotLeadReasonForm} pending=${pending} finish=${finish} note=${handoffNote} />`;

  const lostReasonField = objectMetadata.fields.find((field) => field.name === 'lostReason');
  const options = lostReasonField ? getSortedOptions(lostReasonField) : [];
  return html`<${Modal}
    title=${t('stageTransition.lostTitle')}
    subtitle=${t('stageTransition.lostSubtitle', { record: recordLabel })}
    confirmLabel=${t('stageTransition.confirm')}
    confirmAccent="danger"
    isConfirmDisabled=${!lostReason}
    onClose=${() => finish(null)}
    onConfirm=${() => {
      const extra = { lostReason, ...(lostReasonDetail.trim() ? { lostReasonDetail: lostReasonDetail.trim() } : {}) };
      finish({ apiPatch: { ...patch.apiPatch, ...extra }, localPatch: { ...patch.localPatch, ...extra } });
    }}
  >
    ${handoffNote}
    <label class="stage-transition-field">
      <span>${t('stageTransition.lostReason')}</span>
      <select class="text-input" required value=${lostReason}
        onChange=${(/** @type {Event} */ event) => setLostReason(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
        <option value="">${t('createRecord.choose')}</option>
        ${options.map((option) => html`<option key=${option.value} value=${option.value}>${translateMetadataLabel(option.label)}</option>`)}
      </select>
    </label>
    <label class="stage-transition-field">
      <span>${t('stageTransition.lostReasonDetail')}</span>
      <input class="text-input" value=${lostReasonDetail}
        onInput=${(/** @type {Event} */ event) => setLostReasonDetail(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
    </label>
  <//>`;
}

/**
 * Motivo e detalhe de "não é lead" (o mesmo par de campos serve à conversa).
 * @param {{ objectMetadata: ObjectMetadata, reason: string, detail: string, onReasonChange: (value: string) => void, onDetailChange: (value: string) => void }} props
 */
export function NotLeadReasonFields({ objectMetadata, reason, detail, onReasonChange, onDetailChange }) {
  const reasonField = objectMetadata.fields.find((field) => field.name === 'notLeadReason');
  const options = reasonField ? getSortedOptions(reasonField) : [];
  return html`
    <label class="stage-transition-field">
      <span>${t('notLead.reason')}</span>
      <select class="text-input" required value=${reason} data-testid="not-lead-reason"
        onChange=${(/** @type {Event} */ event) => onReasonChange(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
        <option value="">${t('createRecord.choose')}</option>
        ${options.map((option) => html`<option key=${option.value} value=${option.value}>${translateMetadataLabel(option.label)}</option>`)}
      </select>
    </label>
    <label class="stage-transition-field">
      <span>${t('notLead.reasonDetail')}</span>
      <input class="text-input" value=${detail} data-testid="not-lead-detail"
        onInput=${(/** @type {Event} */ event) => onDetailChange(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
    </label>`;
}

/** @param {{ pending: PendingTransition, finish: (patch: FieldPatch) => void, note?: any }} props */
function NotLeadReasonForm({ pending, finish, note = null }) {
  const { objectMetadata, record, patch } = pending;
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  return html`<${Modal}
    title=${t('notLead.dialogTitle')}
    subtitle=${t('notLead.dialogSubtitle', { record: record?.name ?? '' })}
    confirmLabel=${t('notLead.confirm')}
    isConfirmDisabled=${!reason}
    onClose=${() => finish(null)}
    onConfirm=${() => {
      const extra = { notLeadReason: reason, notLeadReasonDetail: detail.trim() || null };
      finish({ apiPatch: { ...patch.apiPatch, ...extra }, localPatch: { ...patch.localPatch, ...extra } });
    }}
  >
    ${note}
    <${NotLeadReasonFields} objectMetadata=${objectMetadata} reason=${reason} detail=${detail} onReasonChange=${setReason} onDetailChange=${setDetail} />
  <//>`;
}

/** @param {ObjectMetadata} objectMetadata @param {string} value */
function stageLabelOf(objectMetadata, value) {
  const option = objectMetadata.fields.find((field) => field.name === STAGE_FIELD_NAME)?.options?.find((candidate) => candidate.value === value);
  return translateMetadataLabel(option?.label ?? value);
}

/**
 * Aviso de repasse dentro dos diálogos de motivo (Perdido em Pode passar): o card sai da visão depois de gravar.
 * @param {{ record: any, stageLabel: string }} props
 */
function HandoffNote({ record, stageLabel }) {
  return html`<p class="stage-handoff-note" data-testid="handoff-note">
    ${t('stageTransition.handoffConfirmBody', { stage: stageLabel })}
    ${Number(record?.openTaskCount ?? 0) > 0 ? html` ${t('stageTransition.handoffOpenTasks', { count: Number(record.openTaskCount) })}` : null}
  </p>`;
}

/**
 * Confirmação do repasse (Pode passar): o card sai da visão de quem move, então a tela lembra das tarefas abertas e
 * oferece um novo responsável entre quem vê a etapa de destino (o consultor atual pode deixar de ver o card).
 * @param {{ pending: PendingTransition, stageLabel: string, finish: (patch: FieldPatch) => void }} props
 */
function HandoffConfirm({ pending, stageLabel, finish }) {
  const { record, patch, objectMetadata } = pending;
  const [members, setMembers] = useState(/** @type {Array<{ id: string, name: any }> | null} */ (null));
  const [ownerId, setOwnerId] = useState('');
  const hasOwnerField = objectMetadata.nameSingular === 'enrollmentOpportunity' && objectMetadata.fields.some((field) => field.name === 'owner');
  useEffect(() => {
    if (!hasOwnerField || !record?.id) return undefined;
    let isCancelled = false;
    listHandoffMembers(record.id, pending.nextStage)
      .then((loaded) => {
        if (!isCancelled) setMembers(loaded);
      })
      .catch(() => {
        if (!isCancelled) setMembers([]);
      });
    return () => {
      isCancelled = true;
    };
  }, [record?.id, pending.nextStage]);
  const openTaskCount = Number(record?.openTaskCount ?? 0);
  return html`<${Modal}
    title=${t('stageTransition.handoffConfirmTitle')}
    subtitle=${t('stageTransition.handoffConfirmBody', { stage: stageLabel })}
    confirmLabel=${t('stageTransition.handoffConfirm')}
    onClose=${() => finish(null)}
    onConfirm=${() => finish(ownerId && patch
      ? { apiPatch: { ...patch.apiPatch, ownerId }, localPatch: { ...patch.localPatch, ownerId } }
      : patch)}
  >
    ${openTaskCount > 0 ? html`<p class="stage-handoff-note" data-testid="handoff-open-tasks">${t('stageTransition.handoffOpenTasks', { count: openTaskCount })}</p>` : null}
    ${hasOwnerField && members && members.length > 0
      ? html`<label class="stage-transition-field">
          <span>${t('stageTransition.handoffOwner')}</span>
          <select class="text-input" value=${ownerId} data-testid="handoff-owner"
            onChange=${(/** @type {Event} */ event) => setOwnerId(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
            <option value="">${t('stageTransition.handoffKeepOwner')}</option>
            ${members.map((member) => html`<option key=${member.id} value=${member.id}>${formatMemberName(member.name) || t('record.untitled')}</option>`)}
          </select>
          <span class="input-hint">${t('stageTransition.handoffOwnerHint', { stage: stageLabel })}</span>
        </label>`
      : null}
  <//>`;
}
